import moment from "moment-timezone"
import schoolYearRetentionConstants from "@/constants/schoolYearRetentionConstants"

const {
  LAST_DATE_TO_KEEP_DATA_KEY,
  SCHOOL_YEAR_START_MONTH,
  SCHOOL_YEAR_START_DAY,
  MIN_PRIOR_SCHOOL_YEARS,
  MAX_PRIOR_SCHOOL_YEARS
} = schoolYearRetentionConstants

const LAST_DATE_TO_KEEP_DATA_FORMATS = Object.freeze([
  moment.ISO_8601,
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD"
])

/**
 * Backend last_date_to_keep_data is a UTC datetime. Timezone-naive strings
 * (MySQL-style "YYYY-MM-DD HH:mm:ss") must not be read as the browser's local
 * time, or the cutoff shifts for users outside UTC.
 */
const parseLastDateToKeepData = (school) => {
  const rawDate = school?.[LAST_DATE_TO_KEEP_DATA_KEY]

  if (rawDate === null || rawDate === undefined || rawDate === "") {
    return null
  }

  const parsedDate = moment.utc(rawDate, LAST_DATE_TO_KEEP_DATA_FORMATS, true)

  return parsedDate.isValid() ? parsedDate : null
}

const getCurrentSchoolYearStartYear = (now = moment()) => {
  const currentYear = now.year()
  const rolloverDate = moment({
    year: currentYear,
    month: SCHOOL_YEAR_START_MONTH,
    date: SCHOOL_YEAR_START_DAY,
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0
  })

  return now.isSameOrAfter(rolloverDate) ? currentYear : currentYear - 1
}

const getSchoolYearStartYearForDate = (date) => {
  const parsedDate = moment.isMoment(date)
    ? date.clone().utc()
    : moment.utc(date)

  if (!parsedDate.isValid()) {
    return getCurrentSchoolYearStartYear()
  }

  return parsedDate.month() >= SCHOOL_YEAR_START_MONTH
    ? parsedDate.year()
    : parsedDate.year() - 1
}

/**
 * Oldest school-year start (e.g. 2025 for 2025-26) the UI may offer.
 * Missing cutoff falls back to the current year so we never offer years
 * the API will reject.
 */
const resolveOldestSchoolYearStartYear = (
  school,
  currentSchoolYearStartYear = getCurrentSchoolYearStartYear()
) => {
  const lastDate = parseLastDateToKeepData(school)

  if (!lastDate) {
    return currentSchoolYearStartYear
  }

  const oldestStartYear = getSchoolYearStartYearForDate(lastDate)
  const minAllowedStartYear =
    currentSchoolYearStartYear - MAX_PRIOR_SCHOOL_YEARS

  return Math.min(
    currentSchoolYearStartYear,
    Math.max(oldestStartYear, minAllowedStartYear)
  )
}

const resolvePriorSchoolYears = (
  school,
  currentSchoolYearStartYear = getCurrentSchoolYearStartYear()
) => {
  const oldestStartYear = resolveOldestSchoolYearStartYear(
    school,
    currentSchoolYearStartYear
  )

  return Math.max(
    MIN_PRIOR_SCHOOL_YEARS,
    currentSchoolYearStartYear - oldestStartYear
  )
}

const getSchoolYearMinDate = (startYear) => {
  return new Date(startYear, SCHOOL_YEAR_START_MONTH, SCHOOL_YEAR_START_DAY)
}

/**
 * Calendar/API min date for a school year: the later of July 1 and the
 * school's last_date_to_keep_data.
 */
const clampMinDate = (yearMinDate, school) => {
  if (!yearMinDate) {
    return yearMinDate
  }

  const lastDate = parseLastDateToKeepData(school)

  if (!lastDate) {
    return yearMinDate
  }

  const cutoff = lastDate.toDate()

  return cutoff > yearMinDate ? cutoff : yearMinDate
}

const getCalendarMinDate = (selectedSchoolYearLimit, school) => {
  const startYear = selectedSchoolYearLimit?.value?.[0]

  if (!startYear) {
    return null
  }

  return clampMinDate(getSchoolYearMinDate(startYear), school)
}

/**
 * Keep the current selection when it is still retained; otherwise fall back
 * to the current school year so filters never send a year the API rejects.
 */
const resolveSelectedGradeYear = (selectedGradeYear, years) => {
  if (!years?.length) {
    return selectedGradeYear || null
  }

  return (
    years.find((year) => year.label === selectedGradeYear?.label) ||
    years[years.length - 1]
  )
}

export default {
  parseLastDateToKeepData,
  getCurrentSchoolYearStartYear,
  getSchoolYearStartYearForDate,
  resolveOldestSchoolYearStartYear,
  resolvePriorSchoolYears,
  getSchoolYearMinDate,
  clampMinDate,
  getCalendarMinDate,
  resolveSelectedGradeYear
}
